// Trip Purposes Page - Custom trip purposes management
// Migrated from account-settings.ts to be a dedicated page in the main navigation
import { createElement } from '@/utils/dom'
import { authService } from '@/services/auth'
import { SidebarNavigation } from '@/components/navigation'
import { router } from '@/utils/router'

interface TripPurposeData {
  purposes: Array<{ id: number; purpose: string }>
  use_custom_purposes: boolean
  count: number
  min_required: number
  max_allowed: number
}

export class TripPurposesPage {
  private sidebar: SidebarNavigation
  private tripPurposesData: TripPurposeData | null = null
  private llmPrompt: string = ''

  constructor() {
    this.sidebar = new SidebarNavigation({
      currentPage: 'trip-purposes',
      onNavigate: this.handleSidebarNavigation.bind(this)
    })
  }

  async render(): Promise<HTMLElement> {
    const page = createElement('div', { className: 'trip-purposes-page app-layout' })

    // Add sidebar
    page.appendChild(this.sidebar.render())

    // Main content area
    const main = createElement('main', { className: 'main-content' })
    main.appendChild(this.createTripPurposesContent())

    page.appendChild(main)

    // Load data after render
    this.loadTripPurposesData()

    return page
  }

  private handleSidebarNavigation(page: string): void {
    switch (page) {
      case 'dashboard':
        router.navigate('/dashboard')
        break
      case 'statistics':
        router.navigate('/statistics')
        break
      case 'generate':
        router.navigate('/dashboard?action=generate')
        break
      case 'settings':
        router.navigate('/settings')
        break
      case 'whats-new':
        router.navigate('/whats-new')
        break
      case 'admin':
        router.navigate('/admin')
        break
    }
  }

  private createTripPurposesContent(): HTMLElement {
    const container = createElement('div', { className: 'trip-purposes-container' })

    container.innerHTML = `
      <div class="page-header">
        <h1 class="page-title">
          <i class="fa-solid fa-list-check"></i>
          Motivos de Viagem
        </h1>
        <p class="page-description">Personalize os motivos de viagem que aparecem nos seus relatórios</p>
      </div>

      <div class="trip-purposes-content" id="trip-purposes-content">
        <div class="loading-state">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <span>A carregar dados...</span>
        </div>
      </div>

      ${this.getStyles()}
    `

    return container
  }

  private renderTripPurposesSection(): string {
    const data = this.tripPurposesData
    const purposesText = data?.purposes.map(p => p.purpose).join('\n') || ''
    const isEnabled = data?.use_custom_purposes || false
    const count = data?.count || 0
    const minRequired = data?.min_required || 10
    const maxAllowed = data?.max_allowed || 20

    return `
      <div class="settings-section" id="trip-purposes-section">
        <div class="trip-purposes-toggle">
          <label class="toggle-switch">
            <input type="checkbox" id="use-custom-purposes" ${isEnabled ? 'checked' : ''}>
            <span class="toggle-slider"></span>
          </label>
          <span class="toggle-label">
            ${isEnabled ? '✅ A usar motivos personalizados' : '⭕ A usar motivos padrão do sistema'}
          </span>
        </div>

        <div class="llm-prompt-section">
          <div class="prompt-header">
            <h3><i class="fa-solid fa-robot"></i> Gerar Motivos com IA</h3>
            <p>Copie o prompt abaixo e cole numa ferramenta de IA (ChatGPT, Claude, Gemini) para gerar motivos personalizados para o seu setor.</p>
          </div>
          <div class="prompt-container">
            <pre id="llm-prompt-text">${this.escapeHtml(this.llmPrompt)}</pre>
            <button type="button" class="btn btn-secondary btn-sm" id="copy-prompt-btn" title="Copiar Prompt">
              <i class="fa-solid fa-copy"></i>
            </button>
          </div>
        </div>

        <div class="purposes-form">
          <label for="purposes-textarea">
            Motivos de Viagem (um por linha)
            <span class="count-badge ${count >= minRequired ? 'valid' : 'invalid'}">
              ${count}/${maxAllowed}
            </span>
          </label>
          <textarea 
            id="purposes-textarea" 
            placeholder="Reunião com cliente potencial&#10;Visita técnica a obra&#10;Entrega de proposta comercial&#10;..."
            rows="12"
          >${purposesText}</textarea>
          
          <div class="purposes-validation" id="purposes-validation"></div>

          <div class="form-help">
            <p><strong>Requisitos:</strong></p>
            <ul>
              <li>Mínimo: ${minRequired} motivos</li>
              <li>Máximo: ${maxAllowed} motivos</li>
              <li>Cada motivo deve ter entre 2-6 palavras</li>
              <li>Não inclua "regresso" ou "retorno" (adicionado automaticamente)</li>
            </ul>
          </div>
        </div>

        <div class="form-actions">
          <button class="btn btn-primary" id="save-purposes-btn">
            <i class="fa-solid fa-save"></i>
            Guardar Motivos
          </button>
          <button class="btn btn-secondary" id="validate-purposes-btn">
            <i class="fa-solid fa-check-double"></i>
            Validar
          </button>
          <button class="btn btn-danger" id="clear-purposes-btn">
            <i class="fa-solid fa-trash"></i>
            Limpar Todos
          </button>
        </div>
      </div>
    `
  }

  private async loadTripPurposesData(): Promise<void> {
    try {
      const response = await fetch('/api/trip-purposes', {
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      })

      if (response.ok) {
        this.tripPurposesData = await response.json()
      }

      // Also load the LLM prompt
      const promptResponse = await fetch('/api/trip-purposes/prompt', {
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      })

      if (promptResponse.ok) {
        const promptData = await promptResponse.json()
        this.llmPrompt = promptData.prompt || ''
      }

      // Re-render the section with loaded data
      const contentSection = document.querySelector('#trip-purposes-content')
      if (contentSection) {
        contentSection.innerHTML = this.renderTripPurposesSection()
        this.attachEventListeners()
      }
    } catch (error) {
      console.error('Error loading trip purposes:', error)
      const contentSection = document.querySelector('#trip-purposes-content')
      if (contentSection) {
        contentSection.innerHTML = '<div class="error-state"><i class="fa-solid fa-exclamation-circle"></i> Erro ao carregar dados</div>'
      }
    }
  }

  private attachEventListeners(): void {
    const container = document.querySelector('.trip-purposes-container') as HTMLElement
    if (!container) return

    // Copy prompt button
    const copyBtn = container.querySelector('#copy-prompt-btn')
    if (copyBtn) {
      copyBtn.addEventListener('click', async (e) => {
        e.preventDefault()
        e.stopPropagation()

        const preElement = container.querySelector('#llm-prompt-text') as HTMLPreElement
        const textToCopy = preElement?.textContent || this.llmPrompt

        if (!textToCopy || textToCopy.trim() === '') {
          this.showNotification('Prompt não disponível', 'error')
          return
        }

        try {
          // Try the modern clipboard API first
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(textToCopy)
            this.showNotification('Prompt copiado!', 'success')
          } else {
            // Fallback for older browsers or non-secure contexts
            this.fallbackCopyToClipboard(textToCopy)
          }
        } catch (err) {
          console.error('Clipboard error:', err)
          // Try fallback method
          this.fallbackCopyToClipboard(textToCopy)
        }
      })
    }

    // Toggle custom purposes
    container.querySelector('#use-custom-purposes')?.addEventListener('change', async (e) => {
      const enabled = (e.target as HTMLInputElement).checked
      await this.toggleCustomPurposes(enabled)
    })

    // Save purposes button
    container.querySelector('#save-purposes-btn')?.addEventListener('click', async () => {
      await this.saveTripPurposes(container)
    })

    // Validate purposes button
    container.querySelector('#validate-purposes-btn')?.addEventListener('click', async () => {
      await this.validatePurposes(container)
    })

    // Clear purposes button
    container.querySelector('#clear-purposes-btn')?.addEventListener('click', async () => {
      if (confirm('Tem certeza que deseja limpar todos os motivos personalizados?')) {
        await this.clearAllPurposes(container)
      }
    })

    // Real-time count update
    container.querySelector('#purposes-textarea')?.addEventListener('input', (e) => {
      this.updatePurposesCount(container, (e.target as HTMLTextAreaElement).value)
    })
  }

  private updatePurposesCount(container: HTMLElement, text: string): void {
    const lines = text.split('\n').filter(line => line.trim().length > 0)
    const count = lines.length
    const minRequired = this.tripPurposesData?.min_required || 10
    const maxAllowed = this.tripPurposesData?.max_allowed || 20

    const badge = container.querySelector('.count-badge')
    if (badge) {
      badge.textContent = `${count}/${maxAllowed}`
      badge.className = `count-badge ${count >= minRequired && count <= maxAllowed ? 'valid' : 'invalid'}`
    }
  }

  private async toggleCustomPurposes(enabled: boolean): Promise<void> {
    try {
      const response = await fetch('/api/trip-purposes/toggle', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({ enabled })
      })

      const data = await response.json()
      console.log('Toggle response:', response.status, data)

      if (response.ok) {
        this.showNotification(data.message || 'Alteração guardada', 'success')
        if (this.tripPurposesData) {
          this.tripPurposesData.use_custom_purposes = enabled
        }
        // Update toggle label
        const toggleLabel = document.querySelector('.toggle-label')
        if (toggleLabel) {
          toggleLabel.textContent = enabled
            ? '✅ A usar motivos personalizados'
            : '⭕ A usar motivos padrão do sistema'
        }
      } else {
        // Extract error message from various possible response formats
        let errorMsg = 'Erro ao alternar motivos'

        if (typeof data === 'string') {
          errorMsg = data
        } else if (data.message && typeof data.message === 'string') {
          errorMsg = data.message
        } else if (data.error && typeof data.error === 'string') {
          errorMsg = data.error
        } else if (data.message?.message) {
          errorMsg = data.message.message
        }

        // Check if it's the validation error about needing more purposes
        if (errorMsg.includes('necessários') || errorMsg.includes('motivos') || response.status === 400) {
          errorMsg = 'Precisa adicionar pelo menos 10 motivos de viagem antes de ativar'
        }

        this.showNotification(errorMsg, 'error')
        // Revert toggle
        const toggle = document.querySelector('#use-custom-purposes') as HTMLInputElement
        if (toggle) toggle.checked = !enabled
      }
    } catch (error) {
      console.error('Toggle error:', error)
      this.showNotification('Erro de rede ao alternar motivos', 'error')
      // Revert toggle
      const toggle = document.querySelector('#use-custom-purposes') as HTMLInputElement
      if (toggle) toggle.checked = !enabled
    }
  }

  private async saveTripPurposes(container: HTMLElement): Promise<void> {
    const textarea = container.querySelector('#purposes-textarea') as HTMLTextAreaElement
    const purposes = textarea.value.split('\n').filter(line => line.trim().length > 0)

    const minRequired = this.tripPurposesData?.min_required || 10
    const maxAllowed = this.tripPurposesData?.max_allowed || 20

    // Frontend validation before API call
    if (purposes.length < minRequired) {
      this.showNotification(`São necessários pelo menos ${minRequired} motivos para guardar (tem ${purposes.length})`, 'error')
      return
    }

    if (purposes.length > maxAllowed) {
      this.showNotification(`Máximo de ${maxAllowed} motivos permitidos (tem ${purposes.length})`, 'error')
      return
    }

    const saveBtn = container.querySelector('#save-purposes-btn') as HTMLButtonElement
    saveBtn.disabled = true
    saveBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> A guardar...'

    try {
      const response = await fetch('/api/trip-purposes/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          purposes,
          use_custom_purposes: true  // Always enable custom purposes when saving motivos
        })
      })

      if (response.ok) {
        const data = await response.json()
        this.tripPurposesData = data.data
        this.showNotification(data.message || 'Motivos guardados com sucesso!', 'success')

        // Auto-enable the toggle after successfully saving motivos
        const toggle = container.querySelector('#use-custom-purposes') as HTMLInputElement
        if (toggle && !toggle.checked) {
          toggle.checked = true
          // Update the toggle label to show custom purposes are now active
          const toggleLabel = document.querySelector('.toggle-label')
          if (toggleLabel) {
            toggleLabel.textContent = '✅ A usar motivos personalizados'
          }
          // Update local data state
          if (this.tripPurposesData) {
            this.tripPurposesData.use_custom_purposes = true
          }
        }
      } else {
        const errorData = await response.json()
        // Extract proper error message
        let errorMsg = 'Erro ao guardar motivos'
        if (typeof errorData === 'string') {
          errorMsg = errorData
        } else if (errorData.message && typeof errorData.message === 'string') {
          errorMsg = errorData.message
        } else if (errorData.error && typeof errorData.error === 'string') {
          errorMsg = errorData.error
        }
        this.showNotification(errorMsg, 'error')
      }
    } catch (error) {
      this.showNotification('Erro de rede ao guardar motivos', 'error')
    } finally {
      saveBtn.disabled = false
      saveBtn.innerHTML = '<i class="fa-solid fa-save"></i> Guardar Motivos'
    }
  }

  private async validatePurposes(container: HTMLElement): Promise<void> {
    const textarea = container.querySelector('#purposes-textarea') as HTMLTextAreaElement
    const purposes = textarea.value.split('\n').filter(line => line.trim().length > 0)
    const validationDiv = container.querySelector('#purposes-validation') as HTMLElement

    try {
      const response = await fetch('/api/trip-purposes/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({ purposes, use_custom_purposes: true })
      })

      if (response.ok) {
        const data = await response.json()
        const validation = data.validation

        let html = ''
        if (validation.is_valid) {
          html = '<p class="success">✅ Todos os motivos são válidos!</p>'
        }

        if (validation.errors?.length > 0) {
          html += validation.errors.map((e: string) => `<p class="error">❌ ${e}</p>`).join('')
        }

        if (validation.warnings?.length > 0) {
          html += validation.warnings.map((w: string) => `<p class="warning">⚠️ ${w}</p>`).join('')
        }

        validationDiv.innerHTML = html
      }
    } catch (error) {
      validationDiv.innerHTML = '<p class="error">Erro ao validar</p>'
    }
  }

  private async clearAllPurposes(container: HTMLElement): Promise<void> {
    try {
      const response = await fetch('/api/trip-purposes', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.tripPurposesData = null
        this.showNotification(data.message || 'Motivos removidos', 'success')

        // Clear the textarea
        const textarea = container.querySelector('#purposes-textarea') as HTMLTextAreaElement
        if (textarea) textarea.value = ''

        // Uncheck the toggle
        const toggle = container.querySelector('#use-custom-purposes') as HTMLInputElement
        if (toggle) toggle.checked = false

        this.updatePurposesCount(container, '')
      }
    } catch (error) {
      this.showNotification('Erro ao limpar motivos', 'error')
    }
  }

  private showNotification(message: string, type: 'success' | 'error'): void {
    const notification = createElement('div', {
      className: `notification ${type}`
    })

    notification.innerHTML = `
      <i class="fa-solid ${type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}"></i>
      <span>${message}</span>
      <button class="notification-close">
        <i class="fa-solid fa-times"></i>
      </button>
    `

    notification.querySelector('.notification-close')?.addEventListener('click', () => {
      notification.remove()
    })

    document.body.appendChild(notification)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.remove()
    }, 5000)
  }

  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }

  private fallbackCopyToClipboard(text: string): void {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea')
    textArea.value = text

    // Avoid scrolling to bottom
    textArea.style.top = '0'
    textArea.style.left = '0'
    textArea.style.position = 'fixed'
    textArea.style.opacity = '0'

    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      const successful = document.execCommand('copy')
      if (successful) {
        this.showNotification('Prompt copiado!', 'success')
      } else {
        this.showNotification('Falha ao copiar. Selecione o texto manualmente.', 'error')
      }
    } catch (err) {
      console.error('Fallback copy failed:', err)
      this.showNotification('Falha ao copiar. Selecione o texto manualmente.', 'error')
    }

    document.body.removeChild(textArea)
  }

  private getStyles(): string {
    return `
      <style>
        .trip-purposes-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 2rem;
        }

        .page-header {
          margin-bottom: 2rem;
        }

        .page-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.75rem;
          margin-bottom: 0.5rem;
        }

        .page-description {
          color: var(--text-secondary);
          font-size: 1rem;
        }

        .loading-state,
        .error-state {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 3rem;
          color: var(--text-secondary);
          font-size: 1rem;
        }

        .error-state {
          color: #ef4444;
        }

        .trip-purposes-toggle {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: rgba(100, 100, 100, 0.1);
          border-radius: 8px;
        }

        .toggle-switch {
          position: relative;
          width: 50px;
          height: 26px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 26px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        .toggle-switch input:checked + .toggle-slider {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .toggle-switch input:checked + .toggle-slider:before {
          transform: translateX(24px);
        }

        .toggle-label {
          font-weight: 500;
        }

        .llm-prompt-section {
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: rgba(102, 126, 234, 0.1);
          border-radius: 8px;
          border: 1px solid rgba(102, 126, 234, 0.2);
        }

        .llm-prompt-section h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1rem;
          color: #667eea;
        }

        .llm-prompt-section p {
          margin: 0 0 1rem 0;
          font-size: 0.875rem;
          opacity: 0.8;
        }

        .prompt-container {
          position: relative;
        }

        .prompt-container pre {
          background: rgba(0, 0, 0, 0.3);
          padding: 1rem;
          border-radius: 6px;
          font-size: 0.8rem;
          white-space: pre-wrap;
          word-wrap: break-word;
          max-height: 200px;
          overflow-y: auto;
          margin: 0 0 0.5rem 0;
        }

        .purposes-form {
          margin-bottom: 1.5rem;
        }

        .purposes-form label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .count-badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-weight: 600;
        }

        .count-badge.valid {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .count-badge.invalid {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        #purposes-textarea {
          width: 100%;
          padding: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          background: rgba(0, 0, 0, 0.2);
          color: inherit;
          font-family: inherit;
          font-size: 0.9rem;
          line-height: 1.6;
          resize: vertical;
        }

        #purposes-textarea:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
        }

        .purposes-validation {
          margin-top: 0.5rem;
          font-size: 0.875rem;
        }

        .purposes-validation .error {
          color: #ef4444;
        }

        .purposes-validation .warning {
          color: #f59e0b;
        }

        .purposes-validation .success {
          color: #22c55e;
        }

        .form-help {
          margin-top: 1rem;
          padding: 1rem;
          background: rgba(100, 100, 100, 0.1);
          border-radius: 8px;
          font-size: 0.875rem;
        }

        .form-help ul {
          margin: 0.5rem 0 0 1.5rem;
          padding: 0;
        }

        .form-help li {
          margin-bottom: 0.25rem;
        }

        .form-actions {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .btn-sm {
          font-size: 0.875rem;
          padding: 0.5rem 1rem;
        }

        .btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }

        .btn-danger:hover {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
        }
      </style>
    `
  }
}

// Factory function for easier instantiation
export function createTripPurposesPage(): HTMLElement {
  const page = new TripPurposesPage()
  return page.render() as any
}
